<script setup lang="ts">
import { Ref, ref } from 'vue';
import { usePrintLoginCard } from '../../composables/useDownloadLoginCard';
import useQrCode from '../../composables/useQrCode';
import LoginCard from './LoginCard.vue';

type UserForLoginCard = {
  uid: string;
  name: string;
  userName: string;
  password?: string;
  qrToken?: string;
};

type LoginCardUser = UserForLoginCard & {
  qrCodeImageData?: string;
  qrCodeLoginUrl?: string;
};

const props = withDefaults(
  defineProps<{
    users: UserForLoginCard[];
    apps?: string[];
    appUrl?: string;
    fileName?: string;
  }>(),
  {
    apps: () => ['ilteducation'],
    appUrl: '',
    fileName: 'login-cards',
  },
);

const emit = defineEmits(['downloaded']);

const { appLogos, downloadDocsAsPDF, fullAppUrl, iltIcon } = usePrintLoginCard(props.apps, props.appUrl);
const { createLoginLink, createQrCodeAsSVGDataURL } = useQrCode();

const cardUsersPages: Ref<LoginCardUser[][]> = ref([]);
const columns = ref<2 | 3>(3);

/**
 * Renders a hidden login card chip (using the real .login_card-chip/.login_card--cols-3 styles) and checks
 * whether any username/password wraps onto a second line at the 3-column card width — if so, the text is too
 * long to read comfortably as a single-line pill and we should fallback to a 2-column layout
 */
const wouldOverflowAtThreeColumns = (texts: string[]): boolean => {
  if (texts.length === 0) {
    return false;
  }

  const probe = document.createElement('div');
  probe.className = 'login_card login_card--cols-3';
  probe.style.position = 'fixed';
  probe.style.visibility = 'hidden';
  probe.style.left = '-9999px';
  probe.style.top = '0';

  const container = document.createElement('div');
  container.className = 'login_card-info-conatiner';
  probe.appendChild(container);

  const chip = document.createElement('div');
  chip.className = 'login_card-chip';
  container.appendChild(chip);

  document.body.appendChild(probe);

  chip.textContent = 'X';
  const singleLineHeight = chip.scrollHeight;

  const overflows = texts.some((text) => {
    chip.textContent = text;
    return chip.scrollHeight > singleLineHeight + 1; // +1px rounding tolerance
  });

  document.body.removeChild(probe);
  return overflows;
};

/**
 * Download login cards for all users
 * Canvas has a limit for max height
 * html2pdf.js can not render all cards(more than 100) in one pdf document, if they go over the limit pdf will be blank
 * so we split cards into multiple documents and merge them into one pdf
 */
const downloadLoginCard = async () => {
  const usernamesAndPasswords = props.users.flatMap((u) => [u.userName, u.password].filter(Boolean) as string[]);
  columns.value = wouldOverflowAtThreeColumns(usernamesAndPasswords) ? 2 : 3;

  // 3 rows fit an A4 page height regardless of column count; keep the "5 pages per pdf doc" convention
  const ROWS_PER_PAGE = 3;
  const PAGES_PER_DOCUMENT = 5;
  const CARDS_PER_DOCUMENT = columns.value * ROWS_PER_PAGE * PAGES_PER_DOCUMENT;
  cardUsersPages.value.length = 0;
  await Promise.all(
    props.users.map(async (user, idx) => {
      let qrCodeAsSVGDataURL: string | undefined = undefined;
      let qrCodeLoginUrl: string | undefined = undefined;
      if (user.qrToken) {
        qrCodeLoginUrl = createLoginLink(fullAppUrl.value, user.uid, user.qrToken);
        qrCodeAsSVGDataURL = await createQrCodeAsSVGDataURL(qrCodeLoginUrl, iltIcon);
      }

      const docNumber = Math.floor((idx + 1) / CARDS_PER_DOCUMENT);
      if (!cardUsersPages.value[docNumber]) {
        cardUsersPages.value[docNumber] = [] as LoginCardUser[];
      }
      cardUsersPages.value[docNumber].push({
        ...user,
        qrCodeImageData: qrCodeAsSVGDataURL,
        qrCodeLoginUrl: qrCodeLoginUrl,
      });
    }),
  );

  await downloadDocsAsPDF('login-card-pages', props.fileName);
  emit('downloaded');
  cardUsersPages.value.length = 0; // clear pages
};

defineExpose({
  downloadLoginCard,
});
</script>

<template>
  <v-btn @click="downloadLoginCard">downloadLoginCard {{ cardUsersPages }}</v-btn>
  <div v-for="(page, index) in cardUsersPages" :key="index" class="a4-sheet login-card-pages">
    <LoginCard
      v-for="(user, index) in page"
      :key="index"
      :name="user.name"
      :user-name="user.userName ? user.userName : ''"
      :password="user.password"
      :qr-code-image="user.qrCodeImageData"
      :qr-code-login-url="user.qrCodeLoginUrl"
      :app-logo-imgs="appLogos"
      :app-url="fullAppUrl"
      :columns="columns"
    />
  </div>
</template>

<style scoped>
.a4-sheet {
  width: 210mm;
  display: flex;
  flex-direction: row;
  justify-content: start;
  flex-wrap: wrap;
}
</style>
